/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Injectable, Optional } from "@angular/core";
import Logger from "../../../core/libs/logger";
import { BaseViewService } from "./base-view.service";
import { ConnectionServerModel } from "../model/connection-server-model";
import { CertChromeService } from "../../../native-shared/base/cert.service";
import { ChromeServerService } from "../../server-connect/chrome-server.service";
import { BusEvent, EventBusService, clientUtil, TranslateService } from "@html-core";
import { SyncPolicyRedirectClientRuleAction } from "../../jscdk/jscdk-interface";
import { ModalDialogService } from "../commondialog/dialog.service";

@Injectable({
   providedIn: "root"
})
export class ConnectService {
   constructor(
      private baseViewService: BaseViewService,
      private connectionServerModel: ConnectionServerModel,
      @Optional()
      private certService: CertChromeService,
      private chromeServerService: ChromeServerService,
      private eventBusService: EventBusService,
      private modalDialogService: ModalDialogService,
      private translate: TranslateService
   ) {}
   private certRetryCount = 0; //reset after abort or successstatic
   // static certRetryMax = 0;// allow 0 retry,
   static certRetryThreshold = 1; // 1+ certRetryMax of checking would needed for support "certRetryMax" tries
   private errorDialogId = null;
   private certListener = null;

   public gotoServerSelector = () => {
      Logger.info("go to Server Selector");
      this.baseViewService.changeLocation("/serverselect");
   };

   public gotoAddServer = (server) => {
      this.baseViewService.changeLocationWithSearch("/addserver", server);
   };

   public gotoFileAssociationMenu = () => {
      this.baseViewService.changeLocation("/fileassociation");
   };

   public setAdminSettings(setting = [], commonSetting = {}) {
      this.connectionServerModel.googleAdminSettings = setting;
      this.connectionServerModel.googleCommonAdminSettings = commonSetting;
   }

   public setPolicyUrlFilteringRules(commonAdminSettings: object) {
      const action = {
         name: "SyncPolicyRedirectClientRule",
         content: { protocols: [] }
      } as SyncPolicyRedirectClientRuleAction;
      if (
         !!commonAdminSettings &&
         commonAdminSettings.hasOwnProperty("urlFiltering") &&
         commonAdminSettings["urlFiltering"].hasOwnProperty("protocols") &&
         commonAdminSettings["urlFiltering"]["protocols"].length > 0
      ) {
         for (const singleProtocol of commonAdminSettings["urlFiltering"]["protocols"]) {
            let brokerHostname: string;
            let protocol: string;
            let remoteItem: string;
            let clientRules: string;
            let agentRules: string;
            if (
               singleProtocol.hasOwnProperty("brokerHostname") &&
               singleProtocol.hasOwnProperty("protocol") &&
               singleProtocol.hasOwnProperty("remoteItem") &&
               singleProtocol.hasOwnProperty("clientRules") &&
               singleProtocol.hasOwnProperty("agentRules")
            ) {
               brokerHostname = singleProtocol["brokerHostname"];
               protocol = singleProtocol["protocol"];
               remoteItem = singleProtocol["remoteItem"];
               clientRules = singleProtocol["clientRules"];
               agentRules = singleProtocol["agentRules"];
               action.content.protocols.push({ brokerHostname, protocol, remoteItem, agentRules, clientRules });
            }
         }
         action.content.enabled = true;
         action.content.host = "chrome";
         action.content.origin = "client";
      }
      if (action.content.protocols.length > 0) {
         this.eventBusService.dispatch({
            type: "syncPolicyUrlFilteringRules",
            data: action
         });
      } else {
         // Google Admin Policy do not have url filtering client rules
         Logger.info("No client rule is available from the google admin policy.");
      }
   }

   public getURIModel = (launchUri, isEncode = false): Promise<ChromeClient["queryModel"]["uri"]> => {
      const model = {
         host: ""
      };
      let uagUri = new URL(launchUri).search.replace("?data=", "");
      if (!uagUri) {
         return Promise.resolve(model);
      } else if (isEncode) {
         uagUri = decodeURIComponent(uagUri);
      }
      let host = new URL(uagUri).host;

      return new Promise((resolve, reject) => {
         this.chromeServerService.getOriginServer(host).then((o) => {
            // When handle launch client uri, search whether the server is secondary redirected from a load balance server
            // If so, connect to the load balance server and add this server as mid parameter
            if (o) {
               Logger.info(`Server ${host} is stored redirected from ${o}`);
               uagUri = uagUri.replace(host, o) + "&mid=" + host;
               host = o;
            }
            const url = uagUri.replace("https://", "");
            const uagParam = new URL(uagUri).searchParams;
            model["url"] = url;
            model["host"] = host;
            //@ts-ignore
            for (const key of uagParam.keys()) {
               if (key !== "url" && key !== "host") {
                  model[key] = uagParam.get(key);
               }
            }
            Logger.debug(`Host is ${model["host"]}, url is ${model["url"]}`);
            resolve(model);
         });
      });
   };

   private handleResponseURL(url) {
      url = url.replace("https://", "");
      if (url.slice(-1) === "/") {
         url = url.slice(0, -1);
      }
      return url;
   }

   private abortConnection() {
      this.certRetryCount = 0;
      this.eventBusService.dispatch(new BusEvent.AjaxBusyMsg(false));
      if (!this.modalDialogService.isDialogOpen(this.errorDialogId)) {
         this.errorDialogId = this.modalDialogService.showError({
            data: {
               title: this.translate._T("ERROR"),
               content: this.translate._T("IWA_INCOMPATIBLE_SERVER")
            },
            callbacks: {
               confirm: () => {
                  this.gotoServerSelector();
                  this.errorDialogId = null;
               }
            }
         });
      }
   }

   private _connect(brokerUrl, ops) {
      if (!this.certService) {
         this.connectionServerModel.onHostChosen(brokerUrl);
         ops();
         return;
      }
      let checkURL = brokerUrl;
      if (clientUtil.isIwaClient()) {
         checkURL = checkURL.replaceAll(__PROXY_PATH__, "");
         checkURL += __PROXY_PATH__;
      }

      this.certService.isUrlAccessible(brokerUrl).then((args: any) => {
         const isAccessible = args.isUrlAccessible,
            responseURL = args.responseURL;

         this.eventBusService.dispatch({
            type: "connectingBroker",
            data: {
               serverInfo: {
                  serverAddress: brokerUrl,
                  serverId: "",
                  serverType: ""
               }
            }
         });
         if (isAccessible) {
            this.certRetryCount = 0;
            if (responseURL !== "") {
               if (responseURL !== brokerUrl) {
                  Logger.debug("There's a redirection, store the secondary server");
                  this.chromeServerService.registerSecondaryServer({ address: brokerUrl }, responseURL);
               }
               Logger.info(brokerUrl);
               brokerUrl = this.handleResponseURL(responseURL);
               Logger.info(brokerUrl);
            }
            this.connectionServerModel.onHostChosen(brokerUrl);
            ops();
         } else {
            /**
             * Let's try to display the cert page.
             * Note: it is still possible that the server is not available at
             * all.
             */
            Logger.warning("This server cannot be accessible, try to show cert page first!");
            if (!this.certListener) {
               this.certListener = this.eventBusService
                  .listen(BusEvent.ChromeSelfCertAccepted.MSG_TYPE)
                  .subscribe((msg: BusEvent.ChromeSelfCertAccepted) => {
                     // no matter whether the cert accept is done for any of the server, reset the counter for retry
                     this.certRetryCount = 0;
                     const url = this.handleResponseURL(msg.url);
                     // In bug 2976844, see previous connection listener still handle cert accepted message,
                     // to avoid this, check url and unsubscribe this message when done.
                     Logger.info("cert accepted, processed in connection service");
                     if (url === brokerUrl) {
                        this.connectionServerModel.onHostChosen(url);
                        ops();
                        this.certListener.unsubscribe();
                        this.certListener = null;
                     } else {
                        Logger.info("The url jump back is different, navigation happens.");
                        if (msg.param === "uag" || msg.param === "newServer") {
                           this.certListener.unsubscribe();
                           this.certListener = null;
                           Logger.debug("There's a redirection, store the secondary server");
                           this.chromeServerService.registerSecondaryServer({ address: brokerUrl }, url);
                           this._connect(url, ops);
                        }
                     }
                  });
            }

            this.certRetryCount++;
            if (this.certRetryCount <= ConnectService.certRetryThreshold) {
               this.certService.tryToShowCertPage(brokerUrl);
            } else {
               this.certListener.unsubscribe();
               this.certListener = null;
               this.abortConnection();
            }
         }
      });
   }

   public connectToBroker = (brokerUrl) => {
      this._connect(brokerUrl, () => {
         this.baseViewService.changeLocation("/home");
      });
   };

   public setPolicy(brokerUrl, ops) {
      this._connect(brokerUrl, ops);
   }

   public setURI(brokerUrl, ops) {
      this.chromeServerService.registerNewServer({
         address: brokerUrl
      });

      this._connect(brokerUrl, () => {
         this.baseViewService.switchForURI = true;
         this.baseViewService.changeLocation("/home");
         setTimeout(ops);
      });
   }
}
